<template>
  <b-modal
    :id="id"
    title="Conciliação de Cartão (PagBank)"
    size="lg"
    hide-footer
    no-close-on-backdrop
  >
    <div v-if="loading" class="text-center p-5">
      <b-spinner variant="primary" label="Buscando transações no PagBank..." />
      <p class="mt-1 text-muted">Acessando extrato do PagBank...</p>
    </div>

    <div v-else>
      <b-alert show variant="info" class="mb-2">
        <div class="d-flex align-items-center">
          <feather-icon icon="InfoIcon" class="mr-1" />
          <span>
            Selecione abaixo a transação da maquineta que corresponde ao lançamento: <br>
            <strong>{{ transaction ? transaction.descricao : '' }}</strong> - 
            <span class="font-weight-bold">{{ formatCurrencyBrazil(transaction ? transaction.valorTotal : 0) }}</span>
          </span>
        </div>
      </b-alert>

      <div class="mb-2 d-flex justify-content-between align-items-end">
        <div class="d-flex gap-1">
          <b-form-group label="Listar por Período: De" class="mb-0 mr-1">
            <b-form-input v-model="filter.dataInicio" type="date" size="sm" />
          </b-form-group>
          <b-form-group label="Até" class="mb-0">
            <b-form-input v-model="filter.dataFim" type="date" size="sm" />
          </b-form-group>
        </div>
        <b-button variant="outline-primary" size="sm" @click="buscarTransacoes">
          <feather-icon icon="RefreshCwIcon" class="mr-50" /> Atualizar Lista
        </b-button>
      </div>

      <b-table
        :items="charges"
        :fields="fields"
        responsive
        striped
        hover
        bordered
        small
        show-empty
        empty-text="Nenhuma transação de cartão encontrada no período."
        class="mb-0"
      >
        <template #cell(created_at)="data">
          {{ formatDateGrid(data.value) }}
        </template>

        <template #cell(amount)="data">
          <span class="font-weight-bold text-primary">
            {{ formatCurrencyBrazil(data.value.value / 100) }}
          </span>
        </template>

        <template #cell(payment_method)="data">
          <div class="d-flex align-items-center">
            <feather-icon icon="CreditCardIcon" class="mr-50" />
            <span>{{ data.value.type === 'CREDIT_CARD' ? 'Crédito' : 'Débito' }}</span>
            <b-badge v-if="data.value.card" variant="light-secondary" class="ml-50">
              {{ data.value.card.brand }} **** {{ data.value.card.last_digits }}
            </b-badge>
          </div>
        </template>

        <template #cell(actions)="data">
          <b-button
            variant="success"
            size="sm"
            block
            @click="conciliar(data.item)"
          >
            Conciliar
          </b-button>
        </template>
      </b-table>
      
      <div class="text-right mt-2 border-top pt-2">
        <b-button variant="outline-secondary" @click="$bvModal.hide(id)">
          Cancelar
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BTable, BButton, BSpinner, BAlert, BFormGroup, BFormInput, BBadge
} from 'bootstrap-vue'
import mixinsGlobal from '@/mixins'
import axios from '@axios'

export default {
  name: 'ModalConciliacaoCartao',
  components: {
    BModal, BTable, BButton, BSpinner, BAlert, BFormGroup, BFormInput, BBadge
  },
  mixins: [mixinsGlobal],
  props: {
    id: { type: String, required: true },
    transaction: { type: Object, default: null }
  },
  data() {
    const hoje = new Date()
    const seteDiasAtras = new Date()
    seteDiasAtras.setDate(hoje.getDate() - 7)

    return {
      loading: false,
      charges: [],
      filter: {
        dataInicio: seteDiasAtras.toISOString().split('T')[0],
        dataFim: hoje.toISOString().split('T')[0],
        referenceId: ''
      },
      fields: [
        { key: 'created_at', label: 'Data/Hora' },
        { key: 'amount', label: 'Valor' },
        { key: 'payment_method', label: 'Método' },
        { key: 'actions', label: 'Ação', class: 'text-center' }
      ]
    }
  },
  watch: {
    transaction(newVal) {
      if (newVal) {
        this.buscarTransacoes()
      }
    }
  },
  methods: {
    async buscarTransacoes() {
      this.loading = true
      try {
        const response = await axios.get('/api/financeiro/integracao/pagbank/charges', {
          params: {
            dataInicio: this.filter.dataInicio,
            dataFim: this.filter.dataFim
          }
        })
        
        let chargesResult = []
        if (Array.isArray(response.data)) {
          chargesResult = response.data
        } else if (response.data && response.data.charges) {
          chargesResult = response.data.charges
        } else if (response.data && response.data.id) {
          chargesResult = [response.data]
        }

        // Filtra apenas Cartão
        this.charges = chargesResult.filter(c => 
          c.payment_method && (c.payment_method.type === 'CREDIT_CARD' || c.payment_method.type === 'DEBIT_CARD')
        )
      } catch (error) {
        this.MensagemError('Erro', 'Não foi possível buscar as transações no PagBank.')
      } finally {
        this.loading = false
      }
    },

    async conciliar(charge) {
      const confirm = await this.$bvModal.msgBoxConfirm(
        `Deseja vincular este pagamento de ${this.formatCurrencyBrazil(charge.amount.value / 100)} ao lançamento do SYPER?`,
        {
          title: 'Confirmar Conciliação',
          okVariant: 'success',
          okTitle: 'Sim, Conciliar',
          cancelTitle: 'Não',
          centered: true
        }
      )

      if (confirm) {
        this.loading = true
        try {
          await axios.post(`/api/financeiro/integracao/pagbank/conciliar?pagRecId=${this.transaction.id}`, charge)
          this.showMessageSuccess('Sucesso', 'Lançamento conciliado e quitado com sucesso!')
          this.$emit('success')
          this.$bvModal.hide(this.id)
        } catch (error) {
          this.MensagemError('Erro', 'Falha ao processar conciliação.')
        } finally {
          this.loading = false
        }
      }
    }
  }
}
</script>
